import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { examApi, type ApiResponse } from '../services/api';
import { Badge, Button, Card, EmptyState, ErrorState, LoadingState, PageHeader } from '../components/ui';
import { FiBookOpen, FiClock } from 'react-icons/fi';
import AdBanner from '../components/AdBanner';
import { useAuth } from '../hooks/useAuth';
import './ExamSelectionPage.css';
import { formatExamCode } from '../utils/format';

interface ExamSelectionPageProps {
  onStartExam?: (sessionId: string) => void;
  onExit?: () => void;
}

interface ExamType {
  exam_type_id: string;
  name: string;
  description: string;
  icon?: string;
  total_questions: number;
  exam_duration_minutes: number;
  passing_score: number;
  exam_format?: string;
  is_active: boolean;
}

interface StartExamResponse {
  sessionId: string;
}

type MetaIconType = 'questions' | 'duration' | 'passing' | 'format' | 'defaultExam';

const MetaIcon: React.FC<{ type: MetaIconType; className?: string }> = ({ type, className }) => {
  if (type === 'questions') {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M7 6.5H17M7 12H17M7 17.5H13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <rect x="4" y="3.5" width="16" height="17" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
      </svg>
    );
  }
  if (type === 'duration') {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="13" r="7.5" stroke="currentColor" strokeWidth="1.8" />
        <path d="M12 13V9.5M12 13L14.5 15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M9 3.5H15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  if (type === 'passing') {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 21L14.9 15.6L20.9 14.8L16.6 10.8L17.7 4.9L12 7.8L6.3 4.9L7.4 10.8L3.1 14.8L9.1 15.6L12 21Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      </svg>
    );
  }
  if (type === 'format') {
    return (
      <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" strokeWidth="1.8" />
        <path d="M8 9H16M8 12.5H13M8 16H12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4" y="3.5" width="16" height="17" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8 8.5H16M8 12H16M8 15.5H12.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
};

const ExamSelectionPage: React.FC<ExamSelectionPageProps> = ({ onStartExam, onExit }) => {
  const { user } = useAuth();
  const [examTypes, setExamTypes] = useState<ExamType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startingExam, setStartingExam] = useState<string | null>(null);
  const [confirmExamTypeId, setConfirmExamTypeId] = useState<string | null>(null);
  const [selectedMode, setSelectedMode] = useState<'practice' | 'exam'>('exam');

  const showAds = !user?.paid_plan || user.paid_plan === 0;

  useEffect(() => {
    const loadExamTypes = async () => {
      try {
        const response = await examApi.getTypes();
        if (response.success && response.data) {
          setExamTypes(response.data as ExamType[]);
          return;
        }
        setError('시험 유형을 불러올 수 없습니다.');
      } catch {
        setError('시험 유형을 불러오는 중 오류가 발생했습니다.');
      } finally {
        setIsLoading(false);
      }
    };

    loadExamTypes();
  }, []);

  const modeSummary = useMemo(() => {
    return selectedMode === 'exam'
      ? '실전 모드: 시간 제한과 실제 시험 흐름을 그대로 따릅니다.'
      : '연습 모드: 문항 풀이 후 학습 중심으로 빠르게 복습합니다.';
  }, [selectedMode]);

  const getExamFormatLabel = (format?: string): '객관식' | '주관식' => {
    const normalized = (format || '').toLowerCase();
    if (normalized.includes('subjective') || normalized.includes('short') || normalized.includes('essay') || normalized.includes('주관식')) {
      return '주관식';
    }
    return '객관식';
  };

  const handleStartExam = async (examTypeId: string) => {
    setStartingExam(examTypeId);
    try {
      const response = await examApi.start(examTypeId, selectedMode) as { success: boolean; data?: StartExamResponse };
      if (response.success && response.data) {
        const sessionId = response.data.sessionId;
        onStartExam?.(sessionId);
      } else {
        setError('시험을 시작할 수 없습니다.');
      }
    } catch (error) {
      const message = axios.isAxiosError<ApiResponse>(error)
        ? error.response?.data.error?.message
        : undefined;
      setError(message || '시험을 시작하는 중 오류가 발생했습니다.');
    } finally {
      setStartingExam(null);
    }
  };

  const handleOpenStartConfirm = (examTypeId: string) => {
    if (startingExam !== null) return;
    setConfirmExamTypeId(examTypeId);
  };

  const handleConfirmStartExam = async () => {
    if (!confirmExamTypeId) return;
    const examTypeId = confirmExamTypeId;
    setConfirmExamTypeId(null);
    await handleStartExam(examTypeId);
  };

  if (isLoading) {
    return <LoadingState label="시험 목록을 불러오는 중..." />;
  }

  if (error) {
    return (
      <ErrorState
        title="시험 목록을 불러오지 못했습니다"
        description={error}
        action={onExit && <Button variant="secondary" onClick={onExit}>대시보드로 돌아가기</Button>}
      />
    );
  }

  return (
    <div className="exam-select-v2">
      <PageHeader
        title="시험 선택"
        description="응시 모드를 고른 뒤 시험 유형을 선택하세요."
        actions={onExit && (
          <Button variant="secondary" onClick={onExit}>
            대시보드로 돌아가기
          </Button>
        )}
      />

      <section className="exam-select-v2__mode" aria-label="응시 모드">
        <div className="exam-select-v2__segmented" role="radiogroup">
          <button
            type="button"
            role="radio"
            aria-checked={selectedMode === 'practice'}
            className={`exam-select-v2__segment ${selectedMode === 'practice' ? 'is-active' : ''}`}
            onClick={() => setSelectedMode('practice')}
          >
            <FiBookOpen aria-hidden="true" />
            연습 모드
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={selectedMode === 'exam'}
            className={`exam-select-v2__segment ${selectedMode === 'exam' ? 'is-active' : ''}`}
            onClick={() => setSelectedMode('exam')}
          >
            <FiClock aria-hidden="true" />
            실전 모드
          </button>
        </div>
        <p className="exam-select-v2__mode-desc">{modeSummary}</p>
      </section>

      {examTypes.length === 0 ? (
        <Card padding="none" tone="major">
          <EmptyState
            title="응시 가능한 시험이 없습니다"
            description="쿠폰을 등록했다면 관리자에게 시험 활성화를 요청하세요."
          />
        </Card>
      ) : (
        <section className="exam-select-v2__grid">
          {examTypes.map((examType) => (
            <Card key={examType.exam_type_id} padding="lg" shadow="md" hover className="exam-select-v2__exam-card">
              <div className="exam-select-v2__exam-head">
                <div className="exam-select-v2__exam-title-wrap">
                  {examType.icon ? (
                    <span
                      className="exam-select-v2__exam-icon-wrap"
                      tabIndex={0}
                      aria-label={`${examType.name} 설명 보기`}
                      data-tooltip={examType.description}
                    >
                      <img src={examType.icon} alt={examType.name} className="exam-select-v2__exam-icon" />
                    </span>
                  ) : (
                    <span
                      className="exam-select-v2__exam-icon-wrap exam-select-v2__exam-icon-wrap--placeholder"
                      tabIndex={0}
                      aria-label={`${examType.name} 설명 보기`}
                      data-tooltip={examType.description}
                    >
                      <span className="exam-select-v2__exam-icon exam-select-v2__exam-icon--placeholder">
                        <MetaIcon type="defaultExam" className="exam-select-v2__exam-icon-svg" />
                      </span>
                    </span>
                  )}
                  <div>
                    <h3>{examType.name}</h3>
                  </div>
                </div>
                <Badge tone={examType.is_active ? 'success' : 'neutral'}>
                  {examType.is_active ? '응시 가능' : '비활성'}
                </Badge>
              </div>

              <div className="exam-select-v2__meta-grid">
                <div className="exam-select-v2__meta-item">
                  <span className="exam-select-v2__meta-icon" aria-hidden="true">
                    <MetaIcon type="questions" className="exam-select-v2__meta-icon-svg" />
                  </span>
                  <strong>{examType.total_questions}문항</strong>
                </div>
                <div className="exam-select-v2__meta-item">
                  <span className="exam-select-v2__meta-icon" aria-hidden="true">
                    <MetaIcon type="duration" className="exam-select-v2__meta-icon-svg" />
                  </span>
                  <strong>{examType.exam_duration_minutes}분</strong>
                </div>
                <div className="exam-select-v2__meta-item">
                  <span className="exam-select-v2__meta-icon" aria-hidden="true">
                    <MetaIcon type="passing" className="exam-select-v2__meta-icon-svg" />
                  </span>
                  <strong>합격 {examType.passing_score}%</strong>
                </div>
                <div className="exam-select-v2__meta-item">
                  <span className="exam-select-v2__meta-icon" aria-hidden="true">
                    <MetaIcon type="format" className="exam-select-v2__meta-icon-svg" />
                  </span>
                  <strong>{getExamFormatLabel(examType.exam_format)}</strong>
                </div>
              </div>

              <div className="exam-select-v2__exam-actions">
                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  loading={startingExam === examType.exam_type_id}
                  disabled={!examType.is_active || startingExam !== null}
                  onClick={() => handleOpenStartConfirm(examType.exam_type_id)}
                >
                  {startingExam === examType.exam_type_id
                    ? '세션 준비 중...'
                    : `${formatExamCode(examType.exam_type_id)} ${selectedMode === 'practice' ? '연습 시작' : '시험 시작'}`}
                </Button>
              </div>
            </Card>
          ))}
        </section>
      )}

      <AdBanner
        showAds={showAds}
        adSlot="7731352951"
        format="auto"
        className="exam-select-v2__ad"
      />

      {confirmExamTypeId && (
        <div className="exam-select-v2__confirm-overlay" role="dialog" aria-modal="true" aria-labelledby="exam-start-confirm-title">
          <div className="exam-select-v2__confirm-modal">
            <h3 id="exam-start-confirm-title">응시 전 안내</h3>
            <ul>
              <li>시험 시작 후에는 세션이 자동 저장됩니다.</li>
              <li>시간이 종료되면 자동 제출됩니다.</li>
              <li>문항에 따라 복수 정답이 존재할 수 있습니다.</li>
              <li>시험 완료 후 상세 결과와 해설을 확인할 수 있습니다.</li>
            </ul>
            <div className="exam-select-v2__confirm-actions">
              <Button variant="secondary" onClick={() => setConfirmExamTypeId(null)} disabled={startingExam !== null}>
                취소
              </Button>
              <Button variant="primary" onClick={handleConfirmStartExam} loading={startingExam !== null}>
                확인
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamSelectionPage;
